import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Trash2, MoreHorizontal } from 'lucide-react';
import './GroupHeader.css';

/** List (column) title — click-to-inline-edit; also editable via the ⋯ menu's
 *  Rename. `editing` is owned by the parent so the menu can trigger it. */
export function GroupTitle({ group, editing, setEditing, onRename }) {
  const [draft, setDraft] = useState(group.name || '');
  const ref = useRef(null);
  useEffect(() => {
    if (editing) {
      setDraft(group.name || '');
      requestAnimationFrame(() => {
        ref.current?.focus();
        ref.current?.select();
      });
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== group.name) onRename(group.id, trimmed);
  };

  if (editing) {
    return (
      <input
        ref={ref}
        className="task-board-column-title-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      />
    );
  }
  // Click (not drag) the title to inline-edit. The header is the drag handle
  // and the PointerSensor needs 6px of movement to start a drag, so a plain
  // click enters edit mode while a drag still reorders the list. We let
  // pointerdown bubble to the header so you can also grab the title to drag;
  // only the click is stopped so it never doubles as anything else.
  return (
    <span
      className={`task-board-column-title truncate${group.name ? '' : ' task-board-column-title--empty'}`}
      title="Click to rename"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
    >
      {group.name || 'Unnamed list'}
    </span>
  );
}

/** The ⋯ dropdown on a list header: Rename / Delete. */
export function GroupHeaderMenu({ onRename, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className="task-board-col-menu" ref={ref} onPointerDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="task-board-icon-btn"
        title="List actions"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <div className="task-board-col-menu-dropdown" role="menu">
          <button
            type="button"
            className="task-board-col-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onRename();
            }}
          >
            <Pencil size={13} /> Rename
          </button>
          <button
            type="button"
            className="task-board-col-menu-item task-board-col-menu-item--danger"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onDelete();
            }}
          >
            <Trash2 size={13} /> Delete
          </button>
        </div>
      )}
    </div>
  );
}
